import React, { useState } from 'react'
import { Helmet } from 'react-helmet'
import Preloader from '../components/Preloader'
import Footer from '../components/Footer';
import ScrollReveal from '../components/ScrollReveal';
import Header from '../components/Header';
import { FaChevronDown, FaChevronUp, FaTimes, FaCheckCircle, FaMoneyBillWave } from 'react-icons/fa';

const smeRequirements = [
    "Valid Business Registration Documents (Certificate of Incorporation & Commencement of Business).",
    "Valid National Identification of the Proprietor/Directors (Ghana Card).",
    "Bank Statements for the last six (6) months.",
    "Proof of Business Address/Location (Utility Bill or Tenancy Agreement).",
    "One (1) recent passport-sized photographs.",
    "Sketch of direction to business location and residence."
];

const smeFaqs = [
    {
        question: "What is the maximum amount I can access with Speedy Cash?",
        answer: "The loan amount depends on your business turnover and credit assessment. We offer flexible amounts tailored to your specific business needs to help you scale."
    },
    {
        question: "What is the repayment duration?",
        answer: "Our SME loans typically have a repayment period ranging from 1 to 12 months, allowing you enough time to turn over your capital and generate profit."
    },
    {
        question: "How long does approval take?",
        answer: "We understand business moves fast. Once all required documents are submitted, approval and disbursement are typically processed within 24 to 48 hours."
    },
    {
        question: "Do I need a guarantor?",
        answer: "Guarantor requirements vary based on the loan amount and risk profile. In some cases, a guarantor may be required, while others may be secured by business assets."
    }
];

const SmeLoans = () => {
    const [showRequirements, setShowRequirements] = useState(false);
    const [activeFaq, setActiveFaq] = useState(null);

    const toggleFaq = (index) => {
        setActiveFaq(activeFaq === index ? null : index);
    };

    return (
        <div className='font-nixie-one bg-white min-h-screen text-gray-800'>
            <Helmet>
                <title>SME Loans (Speedy Cash) - Querencia Micro Credit</title>
                <meta name="description" content="Apply for SME Loans (Speedy Cash) at Querencia Micro Credit. Get working capital to expand your business with flexible repayment terms." />
                <meta name="keywords" content="sme loans, business loans, micro credit, Ghana, small business, querencia, finance, loans, africa loans, fast cash, money, quick loans, Accra." />
                <meta property="og:title" content="SME Loans (Speedy Cash) - Querencia Micro Credit" />
                <meta property="og:description" content="Apply for SME Loans (Speedy Cash) at Querencia Micro Credit. Get working capital to expand your business with flexible repayment terms." />
            </Helmet>
            <Header />
            <Preloader />
            <main>
                {/* Hero Section */}
                <section className="relative h-[40vh] md:h-[50vh] bg-cover bg-center" style={{ backgroundImage: "url('/assets/img1.jpeg')" }}>
                    <div className="absolute inset-0 bg-blue-900/60 flex items-center justify-center">
                        <ScrollReveal animationClass="animate-fade-up" threshold={0.1} delay={200}>
                            <div className="text-center text-white px-4">
                                <h1 className="text-2xl md:text-3xl font-bold mb-4 capitalize tracking-wide">SME Loans - Speedy Sika</h1>
                                <p className="text-sm md:text-sm max-w-2xl mx-auto font-light">
                                    sme loans designed to provide small and medium-sized enterprises with the working capital they need..
                                </p>
                            </div>
                        </ScrollReveal>
                    </div>
                </section>

                {/* Content Section */}
                <section className="py-16 px-4 container mx-auto max-w-5xl">
                    <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
                        <ScrollReveal animationClass="animate-slide-left" threshold={0.1}>
                            <div>
                                <div className="flex items-center gap-2 mb-4 text-blue-600">
                                    <FaMoneyBillWave className="text-2xl" />
                                    <span className="font-bold uppercase tracking-wider text-sm">Grow Your Business</span>
                                </div>
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Flexible Capital for Your SME Needs</h2>
                                <p className="text-gray-600 font-semibold text-sm leading-relaxed mb-8 text-justify">
                                    Our "Speedy Sika" SME loan is designed to provide small and medium-sized enterprises with the working capital they need to expand operations, purchase stock, or manage cash flow gaps. We understand the unique challenges of running a business in Ghana, which is why our process is streamlined for speed and convenience.
                                </p>
                                <div className='flex flex-col lg:flex-row gap-6 items-start'>
                                    <button
                                        onClick={() => setShowRequirements(true)}
                                        className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-3 px-8 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg flex items-center gap-2"
                                    >
                                        View Requirements
                                    </button>
                                    <button
                                        className="bg-blue-600 hover:bg-blue-700 text-white text-sm capitalize font-semibold py-3 px-8 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg flex items-center gap-2"
                                    >
                                        <a href='/contactus'>apply now</a>
                                    </button>
                                </div>
                            </div>
                        </ScrollReveal>
                        <ScrollReveal animationClass="animate-fade-up" threshold={0.1} delay={200}>
                            <div className="rounded-2xl overflow-hidden shadow-xl h-80 md:h-96 relative group">
                                <div className="absolute inset-0 bg-blue-900/10 group-hover:bg-transparent transition-colors duration-300"></div>
                                <img src="/assets/logo2.jpeg" alt="SME Business Owner" className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" loading='lazy' />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                            </div>
                        </ScrollReveal>
                    </div>

                    {/* FAQ Accordion */}
                    <ScrollReveal animationClass="animate-fade-up" threshold={0.1}>
                        <div className="bg-gray-50 rounded-2xl p-6 md:p-12 border border-gray-100">
                            <h3 className="text-2xl font-bold text-center text-blue-900 mb-8">Frequently Asked Questions</h3>
                            <div className="space-y-4 max-w-3xl mx-auto">
                                {smeFaqs.map((faq, index) => (
                                    <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                        <button
                                            className="w-full px-6 py-4 text-left flex justify-between items-center focus:outline-none hover:bg-gray-50 transition-colors"
                                            onClick={() => toggleFaq(index)}
                                        >
                                            <span className="font-semibold text-gray-800 text-sm md:text-sm">{faq.question}</span>
                                            <span className={`text-blue-600 transition-transform duration-300 ${activeFaq === index ? 'rotate-180' : ''}`}>
                                                {activeFaq === index ? <FaChevronUp /> : <FaChevronDown />}
                                            </span>
                                        </button>
                                        <div
                                            className={`transition-all duration-300 ease-in-out overflow-hidden ${activeFaq === index ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'}`}
                                        >
                                            <div className="px-6 pb-5 text-gray-600 text-sm font-semibold leading-relaxed border-t border-gray-100 pt-2">
                                                {faq.answer}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </ScrollReveal>
                </section>

                {/* Requirements Modal */}
                {showRequirements && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-opacity duration-300">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-fade-up">
                            <div className="bg-blue-900 p-5 flex justify-between items-center">
                                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                    <FaCheckCircle /> SME Loan Requirements
                                </h3>
                                <button onClick={() => setShowRequirements(false)} className="text-white hover:text-gray-200 transition-colors focus:outline-none">
                                    <FaTimes size={20} />
                                </button>
                            </div>
                            <div className="p-6 md:p-8 max-h-[70vh] overflow-y-auto">
                                <ul className="space-y-4">
                                    {smeRequirements.map((req, index) => (
                                        <li key={index} className="flex items-start gap-3 text-gray-700">
                                            <span className="mt-1.5 w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                                            <span className="text-sm font-semibold leading-relaxed">{req}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-8 pt-4 border-t border-gray-100 text-center">
                                    <button
                                        onClick={() => setShowRequirements(false)}
                                        className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-2 px-8 rounded-full transition-colors text-sm"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </main>
            <Footer />
        </div>
    )
}

export default SmeLoans
